import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FlecheEnvoiClair from '../../assets/fleche_envoi_clair.png';
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input/mobile';
import 'react-phone-number-input/style.css'; // Import default styles
import { styled } from 'styled-components';

import {
  Container,
  ContentWrapper,
  ReturnButton,
  LogoImage,
  ChatBubbleContainer,
  ChatText,
  LoadingOverlay,
  LogoBubbleWrapper
} from './styles';
// Keep InputContainer and InputWrapper for layout consistency, or adjust as needed
import {
  InputContainer,
  InputWrapper,
  // StyledInput, // Remove - replaced by PhoneInput
} from './stylesPhoneSelection';

import compassImage from '../../assets/meo_write.png';
import useDeviceType from '../useDeviceType';
import ProgressBar from '../ProgressBar';
import { useAuth } from '../../contexts/AuthContext';
import apiCall from '../../utils/api';
import theme from '../../theme';

// --- Styled SubmitButton (remains the same, but margin will be removed) ---
const SubmitButton = styled.button`
  /* margin: 0 auto; */ /* Removed, centering handled by PhoneInputSectionWrapper */
  width: 90%;
  font-family: ${props => props.fontFamily || theme.fonts.main};
  font-size: ${props => props.fontSize || theme.fontSizes.medium};
  font-weight: ${props => props.fontWeight || theme.fontWeights.semiBold};
  cursor: pointer;
  transition: all 0.3s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  max-width: 600px;
  border-radius: 15px;
  transition: all 0.15s ease; /* fast transition */

  height: 53px;
  background-color: ${props => (props.disabled ? '#efecec' : '#ff2768')};
  /* Remove shadow if the button is disabled OR pressed (active) */
  box-shadow: ${props =>
    props.disabled || props.active ? 'none' : '0px 4px 0px #d42359'};

  display: flex;
  align-items: center;
  justify-content: center;

  ${props => props.active && `
    transform: translateY(2px);
    /* The box-shadow will be 'none' due to inline style in JSX when active */
  `}
`;

// Wrapper for the phone input and submit button section
const PhoneInputSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center; /* Vertically center content */
  align-items: center;   /* Horizontally center content */
  flex-grow: 1;          /* Take remaining vertical space */
  width: 100%;
  gap: ${theme.spacing.large}; /* Space between phone input area and button */
  padding-bottom: 70px; /* Ensure space from bottom if submit button was fixed */
`;

// --- Custom Styling Wrapper for PhoneInput (Optional but Recommended) ---
// Apply similar styles as StyledInput or adjust as needed
const StyledPhoneInputWrapper = styled(InputWrapper)`
  .PhoneInput {
    /* Style the container if needed */
    display: flex;
    align-items: center;
  }

  .PhoneInputInput {
    /* Style the input field itself - mimic StyledInput */
    flex-grow: 1;
    border: none; // Remove default border
    outline: none; // Remove default outline
    font-family: ${theme.fonts.main};
    font-size: ${theme.fontSizes.large};
    font-weight: ${theme.fontWeights.medium};
    color: ${theme.colors.text};
    background-color: transparent;
    padding: 10px 5px; // Adjust padding as needed
    margin-left: 10px; // Space between flag and number
    width: 100%; // Ensure it takes available space

    /* Add valid/invalid styling if desired */
    border-bottom: 2px solid ${props => (props.isValid ? theme.colors.primary : theme.colors.lightGray)};
    &:focus {
      border-bottom-color: ${theme.colors.primary};
    }
  }

  .PhoneInputCountryIcon {
     /* Adjust flag size/style if needed */
     width: 35px; // Example size
     height: auto;
     box-shadow: none; // Remove library default shadow if present
  }

  /* Style the country select dropdown if needed */
  .PhoneInputCountrySelect {
      /* Example */
      font-size: ${theme.fontSizes.small};
  }

  .error-message {
      color: red;
      font-size: ${theme.fontSizes.small};
      margin-top: 5px;
      text-align: center; /* Center align error message */
      width: 100%;
  }
`;

const PhoneSelection = () => {
  const navigate = useNavigate();
  const url = window.location.href;
  const modifyProfile = url.includes('profile/modify');
  // Store phone number in E.164 format (e.g., +33612345678) or undefined
  const [phoneNumber, setPhoneNumber] = useState();
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(true);
  const [text, setText] = useState('');
  const fullText = "Quel est ton numéro de téléphone portable ?";
  const { isMobile } = useDeviceType();
  const inputRef = useRef(null); // Ref can still be used for focusing PhoneInput
  const { checkAuth } = useAuth();
  const [submitActive, setSubmitActive] = useState(false);

  useEffect(() => {
    // Set initial phone value if in modify profile mode
    if (modifyProfile) {
      const user = JSON.parse(localStorage.getItem('user'));
      // IMPORTANT: Assume user.telephone is stored in E.164 format now
      // If it's stored in national format, you'll need to convert it here
      // or ideally, update the backend/storage to use E.164 format.
      if (user && user.telephone) {
         handlePhoneInputChange(user.telephone); // Update state and validate
      }
    }

    // Typing effect remains the same
    let currentIndex = 0;
    const typingInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        setIsTyping(false);
        clearInterval(typingInterval);
        if (inputRef.current) {
            // Focus the input field within PhoneInput
            inputRef.current.focus();
        }
      }
    }, 15);

    return () => clearInterval(typingInterval);
  }, [modifyProfile]); // Removed dependency on checkAuth if not needed here

  const handlePhoneInputChange = (value) => {
    setPhoneNumber(value); // value is E.164 string or undefined
    console.log("PhoneInput Value (E.164):", value); // Log 1: Voir la valeur reçue
  
    let valid = false;
    let isMobilePhone = false;
  
    if (value) { // Vérifier si value n'est pas undefined
        const isGenerallyValid = isValidPhoneNumber(value);
        console.log("Is Generally Valid:", isGenerallyValid); // Log 2: Résultat de isValidPhoneNumber
  
        if (isGenerallyValid) {
            const parsed = parsePhoneNumber(value);
            console.log("Parsed Number Object:", parsed); // Log 3: Voir l'objet parsé
  
            if (parsed) {
                const numberType = parsed.getType();
                console.log("Detected Number Type:", numberType); // Log 4: Voir le type détecté !
  
                // Vérification si c'est MOBILE ou FIXED_LINE_OR_MOBILE
                if (numberType === 'MOBILE' || numberType === 'FIXED_LINE_OR_MOBILE') {
                     isMobilePhone = true;
                     console.log("Type is considered Mobile."); // Log 5
                } else {
                     console.log("Type is NOT considered Mobile."); // Log 6
                }
            } else {
                console.log("Parsing failed."); // Log 7
            }
            valid = isMobilePhone; // La validité finale dépend du type mobile
        } else {
            console.log("Number is not generally valid according to library."); // Log 8
            valid = false;
        }
    } else {
        console.log("Value is undefined or empty."); // Log 9
        valid = false;
    }
  
    console.log("Setting isValid state to:", valid); // Log 10: Quelle est la valeur finale de valid ?
    setIsValid(valid);
  };

  const handleSubmit = async () => {
    if (!isValid || isTyping || isLoading) return;
    setIsLoading(true);

    // phoneNumber state already holds the E.164 format
    const e164PhoneNumber = phoneNumber;

    try {
      if (modifyProfile) {
        await apiCall(`${process.env.REACT_APP_BACKEND_URL}/auth/user/update/`, 'PATCH', {
          // Send E.164 format to backend
          phone_number: e164PhoneNumber
        });

        var userData = JSON.parse(localStorage.getItem('user'));
        // Store E.164 format in local storage
        userData.telephone = e164PhoneNumber;
        localStorage.setItem('user', JSON.stringify(userData));
        console.log("Téléphone mis à jour (E.164):", e164PhoneNumber);

        await checkAuth();
        navigate('/profile');

      } else {
        // Original onboarding flow
        // Store E.164 format
        localStorage.setItem('user_phone', e164PhoneNumber);
        navigate('/presentation-features');
      }
    } catch (error) {
      console.error('Error saving phone number:', error);
      // Optionally: Show an error message to the user
    } finally {
      setIsLoading(false);
    }
  };

  // handleBack remains the same
  const handleBack = () => {
    if (modifyProfile) {
      navigate('/profile');
    } else {
      navigate('/origine-city');
    }
  };

  // Submit Button Handlers remain the same
   const handleSubmitPressStart = () => {
    if (!isValid || isTyping || isLoading) return;
    setSubmitActive(true);
  };

  const handleSubmitPressEnd = () => {
    if (!isValid || isTyping || isLoading) return;
    setSubmitActive(false);
    // Delay navigation briefly to allow the button state to be visible
    setTimeout(() => {
      handleSubmit();
    }, 150); // Adjust delay as needed
  };

  return (
    <Container>
      {isLoading && <LoadingOverlay isLoading={isLoading} />}
      <ReturnButton onClick={handleBack}>
        <img src={FlecheEnvoiClair} alt="Back" style={{ width: '24px', height: '24px' }} />
      </ReturnButton>
      <ContentWrapper>
        {!modifyProfile && <ProgressBar startPercent={90} endPercent={100} />}
        <LogoBubbleWrapper>
          <LogoImage src={compassImage} alt="Compass Logo" />
          <ChatBubbleContainer isMobile={isMobile}>
            <ChatText isVisible={text.length > 0}>
              <span>{text}</span>
            </ChatText>
          </ChatBubbleContainer>
        </LogoBubbleWrapper>

        <PhoneInputSectionWrapper>
          <InputContainer
            style={{
              opacity: isTyping ? 0 : 1,
              transition: 'opacity 0.3s ease',
              pointerEvents: isTyping ? 'none' : 'auto'
            }}
          >
            <StyledPhoneInputWrapper isValid={isValid}>
               <PhoneInput
                  ref={inputRef}
                  international
                  countryCallingCodeEditable={false}
                  defaultCountry="FR"
                  placeholder="Numéro de mobile"
                  value={phoneNumber}
                  onChange={handlePhoneInputChange}
                  disabled={isTyping}
                  />
              {phoneNumber && !isValid && (
                <div className="error-message">
                  Veuillez entrer un numéro de mobile valide.
                </div>
              )}
            </StyledPhoneInputWrapper>
          </InputContainer>

          <SubmitButton
            type="button"
            disabled={isLoading || isTyping || !isValid}
            style={{
              opacity: (isTyping || !isValid) ? 0.5 : 1,
              cursor: (isTyping || !isValid) ? 'not-allowed' : 'pointer',
              // boxShadow is managed by styled-component based on disabled/active props
            }}
            onMouseDown={handleSubmitPressStart}
            onMouseUp={handleSubmitPressEnd}
            onMouseLeave={() => setSubmitActive(false)}
            onTouchStart={handleSubmitPressStart}
            onTouchEnd={handleSubmitPressEnd}
            active={submitActive}
          >
            {isLoading ? 'Chargement...' : modifyProfile ? 'Valider' : 'Continuer'}
          </SubmitButton>
        </PhoneInputSectionWrapper>
      </ContentWrapper>
    </Container>
  );
};

export default PhoneSelection;
